import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertTriangle,
  Shield,
  Heart,
  CloudRain,
  Globe,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Bell,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

interface SOSAlert {
  id: string;
  country_name: string;
  country_code: string | null;
  alert_type: string;
  severity: string;
  title: string;
  description: string | null;
  source: string | null;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  created_at: string;
}

const SOSAlertsManagementTab = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState<SOSAlert[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState<SOSAlert | null>(null);
  const [formData, setFormData] = useState({
    country_name: "",
    country_code: "",
    alert_type: "security",
    severity: "medium",
    title: "",
    description: "",
    source: "",
    valid_from: "",
    valid_until: "",
    is_active: true,
  });

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("sos_alerts")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      toast({ variant: "destructive", title: "Error", description: error.message });
    } else {
      setAlerts(data || []);
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      country_name: "",
      country_code: "",
      alert_type: "security",
      severity: "medium",
      title: "",
      description: "",
      source: "",
      valid_from: "",
      valid_until: "",
      is_active: true,
    });
    setEditingAlert(null);
  };

  const handleEdit = (alert: SOSAlert) => {
    setEditingAlert(alert);
    setFormData({
      country_name: alert.country_name,
      country_code: alert.country_code || "",
      alert_type: alert.alert_type,
      severity: alert.severity,
      title: alert.title,
      description: alert.description || "",
      source: alert.source || "",
      valid_from: alert.valid_from ? alert.valid_from.split("T")[0] : "",
      valid_until: alert.valid_until ? alert.valid_until.split("T")[0] : "",
      is_active: alert.is_active,
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.country_name || !formData.title) {
      toast({ variant: "destructive", title: "Error", description: "Country and title are required" });
      return;
    }

    const payload = {
      country_name: formData.country_name,
      country_code: formData.country_code || null,
      alert_type: formData.alert_type,
      severity: formData.severity,
      title: formData.title,
      description: formData.description || null,
      source: formData.source || null,
      valid_from: formData.valid_from || null,
      valid_until: formData.valid_until || null,
      is_active: formData.is_active,
    };

    let error;
    if (editingAlert) {
      ({ error } = await supabase.from("sos_alerts").update(payload).eq("id", editingAlert.id));
    } else {
      ({ error } = await supabase.from("sos_alerts").insert(payload));
    }

    if (error) {
      toast({ variant: "destructive", title: "Error", description: error.message });
    } else {
      toast({ title: "Success", description: editingAlert ? "Alert updated" : "Alert created" });
      setIsDialogOpen(false);
      resetForm();
      fetchAlerts();
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this alert?")) return;

    const { error } = await supabase.from("sos_alerts").delete().eq("id", id);
    if (error) {
      toast({ variant: "destructive", title: "Error", description: error.message });
    } else {
      toast({ title: "Success", description: "Alert deleted" });
      fetchAlerts();
    }
  };

  const toggleActive = async (alert: SOSAlert) => {
    const { error } = await supabase
      .from("sos_alerts")
      .update({ is_active: !alert.is_active })
      .eq("id", alert.id);

    if (error) {
      toast({ variant: "destructive", title: "Error", description: error.message });
    } else {
      fetchAlerts();
    }
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case "security":
        return <Shield className="h-4 w-4" />;
      case "health":
        return <Heart className="h-4 w-4" />;
      case "weather":
        return <CloudRain className="h-4 w-4" />;
      case "political":
        return <AlertTriangle className="h-4 w-4" />;
      default:
        return <Globe className="h-4 w-4" />;
    }
  };

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case "critical":
        return <Badge className="bg-red-600 text-white">Critical</Badge>;
      case "high":
        return <Badge className="bg-red-100 text-red-700 border-red-200">High</Badge>;
      case "medium":
        return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Medium</Badge>;
      default:
        return <Badge className="bg-green-100 text-green-700 border-green-200">Low</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              SOS Alerts Management
            </CardTitle>
            <CardDescription>
              Create and manage global safety alerts for all destinations
            </CardDescription>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Alert
          </Button>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Valid</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {alerts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No alerts configured. Create your first alert to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  alerts.map((alert) => (
                    <TableRow key={alert.id} className={!alert.is_active ? "opacity-50" : ""}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getAlertIcon(alert.alert_type)}
                          <span className="capitalize text-sm">{alert.alert_type}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="font-medium">{alert.country_name}</span>
                          {alert.country_code && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              {alert.country_code}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">{alert.title}</TableCell>
                      <TableCell>{getSeverityBadge(alert.severity)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {alert.valid_from
                          ? format(new Date(alert.valid_from), "MMM d")
                          : "-"}{" "}
                        →{" "}
                        {alert.valid_until
                          ? format(new Date(alert.valid_until), "MMM d")
                          : "ongoing"}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={alert.is_active}
                          onCheckedChange={() => toggleActive(alert)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(alert)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(alert.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAlert ? "Edit Alert" : "Create New Alert"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Country Name *</Label>
                <Input
                  value={formData.country_name}
                  onChange={(e) => setFormData({ ...formData, country_name: e.target.value })}
                  placeholder="Spain"
                />
              </div>
              <div className="space-y-2">
                <Label>Country Code</Label>
                <Input
                  value={formData.country_code}
                  onChange={(e) =>
                    setFormData({ ...formData, country_code: e.target.value.toUpperCase() })
                  }
                  placeholder="ES"
                  maxLength={3}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Severe weather warning"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Alert Type</Label>
                <Select
                  value={formData.alert_type}
                  onValueChange={(v) => setFormData({ ...formData, alert_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="security">Security</SelectItem>
                    <SelectItem value="health">Health</SelectItem>
                    <SelectItem value="weather">Weather</SelectItem>
                    <SelectItem value="political">Political</SelectItem>
                    <SelectItem value="travel">Travel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Severity</Label>
                <Select
                  value={formData.severity}
                  onValueChange={(v) => setFormData({ ...formData, severity: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Detailed information about the alert..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Source</Label>
              <Input
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                placeholder="Ministry of Foreign Affairs"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Valid From</Label>
                <Input
                  type="date"
                  value={formData.valid_from}
                  onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Valid Until</Label>
                <Input
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(c) => setFormData({ ...formData, is_active: c })}
              />
              <Label>Active</Label>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSave}>{editingAlert ? "Update" : "Create"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SOSAlertsManagementTab;
